import { useState, useEffect } from 'react';
import { Loader2, Plus, ChevronLeft, ChevronRight, Send, Mail, MailX } from 'lucide-react';
import { listTransmissions, listMemberships } from '../services/api';
import { useAuthStore } from '../store/authStore';
import ArtistDashboardNav from '../components/dashboard/ArtistDashboardNav';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';
import { buildTenantPath } from '../hooks/useTenant';

interface Transmission {
  id: number;
  tenantId: number;
  headline: string;
  content: string | null;
  status: string;
  transmissionDate: number | null;
  memberships: string | null;
  sentAsEmail: number;
  bannerImage: string | null;
  createdAt: number;
  updatedAt: number;
}

interface Membership {
  id: number;
  name: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function TransmissionsPage({ slug }: { slug: string }) {
  const { token } = useAuthStore();
  const onboardingProgress = useOnboardingProgress(slug);
  const [transmissions, setTransmissions] = useState<Transmission[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);

  const fetchTransmissions = async (page = 1) => {
    try {
      const res = await listTransmissions(slug, { page, limit: 20 });
      if (res.ok) {
        const json = await res.json() as { data: Transmission[]; pagination: PaginationData };
        setTransmissions(json.data);
        setPagination(json.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch transmissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberships = async () => {
    try {
      const res = await listMemberships(slug);
      if (res.ok) {
        const json = await res.json() as { data: Membership[] };
        setMemberships(json.data);
      }
    } catch {}
  };

  useEffect(() => {
    fetchTransmissions();
    fetchMemberships();
  }, [slug]);

  const getMembershipNames = (membershipsJson: string | null): string => {
    if (!membershipsJson) return '—';
    try {
      const ids: number[] = JSON.parse(membershipsJson);
      if (!Array.isArray(ids) || ids.length === 0) return '—';
      return ids.map(id => {
        const m = memberships.find(mem => mem.id === id);
        return m ? m.name : `#${id}`;
      }).join(', ');
    } catch {
      return '—';
    }
  };

  const formatDate = (ts: number | null): string => {
    if (!ts) return '—';
    return new Date(ts * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      PUBLISHED: 'bg-green-500/20 text-green-400 border-green-500/30',
      ARCHIVED: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border ${colors[status] || colors.DRAFT}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <ArtistDashboardNav slug={slug} currentPage="transmissions" onboardingProgress={onboardingProgress} />

      <main className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Transmissions</h1>
            <p className="text-sm text-text-secondary mt-1 font-mono">Broadcast to your fans</p>
          </div>
          <a
            href={buildTenantPath(slug, 'transmissions/new')}
            className="btn-primary text-sm !px-4 !py-2 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Transmission
          </a>
        </div>

        {/* Table */}
        {transmissions.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Send className="w-12 h-12 text-electric-cyan/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No transmissions yet</h3>
            <p className="text-text-secondary text-sm mb-6">Create your first transmission to broadcast to your fans.</p>
            <a
              href={buildTenantPath(slug, 'transmissions/new')}
              className="btn-primary text-sm !px-4 !py-2 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Transmission
            </a>
          </div>
        ) : (
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Date</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Headline</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Status</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Memberships</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {transmissions.map((t) => (
                    <tr
                      key={t.id}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                      onClick={() => { window.location.href = buildTenantPath(slug, `transmissions/${t.id}`); }}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-text-secondary whitespace-nowrap">
                        {formatDate(t.transmissionDate)}
                      </td>
                      <td className="px-4 py-3 font-medium text-white">
                        {t.headline}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(t.status)}
                      </td>
                      <td className="px-4 py-3 text-xs text-text-secondary max-w-[200px] truncate">
                        {getMembershipNames(t.memberships)}
                      </td>
                      <td className="px-4 py-3">
                        {t.sentAsEmail ? (
                          <Mail className="w-4 h-4 text-electric-cyan" />
                        ) : (
                          <MailX className="w-4 h-4 text-text-muted" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
                <span className="text-xs text-text-secondary font-mono">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchTransmissions(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="p-1.5 rounded border border-white/10 hover:border-electric-cyan/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => fetchTransmissions(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="p-1.5 rounded border border-white/10 hover:border-electric-cyan/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
